import React, {
    FunctionComponent,
    useState,
    ChangeEvent,
    useEffect,
} from 'react'
import { useTranslation } from 'react-i18next'
import some from 'lodash/some'
import { Step2Props } from './types'
import { Step2ValueModel } from '../types'

import { AccountType } from '../../shared/types'
import { validateStep2, validateAssociationName } from '../Validation'
import {
    validateAccountType,
    validateBg,
    validatePg,
    validateStreetBox,
    validateZipCode,
    validateCity,
    validateVat,
} from '../../shared/Validation';
import CompanyRegistrationsService from "@apps/Shared/Services/CompanyRegistrations/CompanyRegistrations.service";
import { ICustomerCompanyInfoResponse } from "@apps/Shared/Services/CompanyRegistrations/interfaces";
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import { getAllErrors, removeAllErrorsForKey, removeError } from "@sharedComponents/PantaForm/utils";
import LoadingIndicator from "@sharedComponents/LoadingIndicator";
import InputText from "@sharedComponents/InputText";
import InputToggleButton from "@sharedComponents/InputToggleButton";
import Alert from "@sharedComponents/Alert";
import Button from "@sharedComponents/Button";
import {ButtonType} from '@sharedComponents/Button/types'
import Icon from "@sharedComponents/Icon";

const Step2: FunctionComponent<Step2Props> = props => {
    const { t } = useTranslation()

    const [loadingCompanyInfo, setLoadingCompanyInfo] = useState<boolean>(false)

    const [formErrors, setFormErrors] = useState<FormErrorType[]>([])

    const allErrors = getAllErrors(formErrors)

    const [stepValues, setStepValues] = useState<Step2ValueModel>({
        organizationNumber: props.values.organizationNumber || '',
        registeredForFTax: 'false',
        registeredForVAT: 'false',
        vatNumber: '',
    })

    const disableAssociationName = !!props.values.existingInfo?.name
    const disableVat = !!props.values.existingInfo?.hasVat
    const disableVatNumber = !!(
        props.values.existingInfo?.hasVat &&
        props.values.existingInfo?.vatNumber
    )

    const rehydrate = (values: any) => {
        if (values) {
            setStepValues({
                ...stepValues,
                organizationNumber: values.organizationNumber || '',
                associationName: values.associationName || '',
                registeredForFTax: values.registeredForFTax || 'false',
                registeredForVAT: values.registeredForVAT || 'false',
                vatNumber: values.vatNumber || '',
                accountType: values.accountType || undefined,
                bg: values.bg || '',
                pg: values.pg || '',
                careOf: values.careOf || '',
                streetBox: values.streetBox || '',
                zipCode: values.zipCode || '',
                city: values.city || '',
            })
        }
    }

    useEffect(() => {
        rehydrate(props.values)
    }, [props.rehydrateFormKey])

    useEffect(() => {
        if (!props.values.requestInfo) {
            if (props.values?.existingInfo) {
                const {
                    name,
                    hasVat,
                    vatNumber,
                    postalAddress,
                } = props.values.existingInfo
                const newValues = {
                    organizationNumber: props.values.organizationNumber,
                    associationName: props.values.associationName || name,
                    registeredForFTax:
                        props.values.registeredForFTax || 'false',
                    registeredForVAT: hasVat ? 'true' : 'false',
                    vatNumber: vatNumber,
                    accountType: props.values.accountType || undefined,
                    bg: props.values.bg || '',
                    pg: props.values.pg || '',
                    careOf: props.values.careOf || postalAddress?.street2,
                    streetBox: props.values.streetBox || postalAddress?.street1,
                    zipCode: props.values.zipCode || postalAddress?.zipCode,
                    city: props.values.city || postalAddress?.city,
                }
                props.onChangeValues && props.onChangeValues(newValues)
                rehydrate(newValues)
            }
            return
        }

        setLoadingCompanyInfo(true)

        CompanyRegistrationsService.GetCustomerCompanyInfo(
            props.values.organizationNumber
        )
            .then(result => {
                if (result && result.status === 200) {
                    const company: ICustomerCompanyInfoResponse = result.data

                    const map: Step2ValueModel = {
                        organizationNumber: stepValues.organizationNumber,
                        registeredForVAT:
                            company.registeredForVAT === null
                                ? 'false'
                                : company.registeredForVAT === true
                                ? 'true'
                                : 'false',
                        registeredForFTax:
                            company.registeredForFTax === null
                                ? 'false'
                                : company.registeredForFTax === true
                                ? 'true'
                                : 'false',
                        vatNumber: company.vatNumber || '',
                        associationName: company.companyName,
                        streetBox: company.streetAddress,
                        zipCode: company.zipCode,
                        city: company.city,
                    }

                    setStepValues(map)

                    props.onChangeValues && props.onChangeValues(map)
                }

                setLoadingCompanyInfo(false)
            })
            .catch(error => {
                console.log(error)

                setLoadingCompanyInfo(false)
            })

        rehydrate(props.values)
    }, [])

    const onChangeAssociationName = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        setStepValues({ ...stepValues, associationName: value })
        props.onChangeValues && props.onChangeValues({ associationName: value })
        setFormErrors(validateAssociationName(value, formErrors))
    }

    const onChangeFTax = (value: string) => {
        setStepValues({ ...stepValues, registeredForFTax: value })

        props.onChangeValues &&
            props.onChangeValues({ registeredForFTax: value })
    }

    const onChangeRegisteredForVat = (value: string) => {
        setStepValues({ ...stepValues, registeredForVAT: value, vatNumber: '' })
        props.onChangeValues &&
            props.onChangeValues({ registeredForVAT: value, vatNumber: '' })
        setFormErrors(
            removeError(formErrors, 'vatNumber', t('error.emptyVatnr'))
        )
    }

    const onChangeVatNumber = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        setStepValues({ ...stepValues, vatNumber: value })
        props.onChangeValues && props.onChangeValues({ vatNumber: value })
        setFormErrors(validateVat(value, formErrors))
    }

    const onChangeAccountType = (value: AccountType) => {
        setStepValues({
            ...stepValues,
            accountType: value,
            bg: '',
            pg: '',
        })

        props.onChangeValues && props.onChangeValues({ accountType: value })

        let errors = formErrors

        errors = removeAllErrorsForKey(errors, 'bg')
        errors = removeAllErrorsForKey(errors, 'pg')
        errors = validateAccountType(value, true, errors)

        setFormErrors(errors)
    }

    const onChangeBg = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim()

        setStepValues({ ...stepValues, bg: value })
        props.onChangeValues && props.onChangeValues({ bg: value })
        setFormErrors(validateBg(value, true, formErrors))
    }

    const onChangePg = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim()

        setStepValues({ ...stepValues, pg: value })
        props.onChangeValues && props.onChangeValues({ pg: value })
        setFormErrors(validatePg(value, true, formErrors))
    }

    const onChangeCareOf = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        setStepValues({ ...stepValues, careOf: value })
        props.onChangeValues && props.onChangeValues({ careOf: value })
    }

    const onChangeStreetBox = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        setStepValues({ ...stepValues, streetBox: value })
        props.onChangeValues && props.onChangeValues({ streetBox: value })
        setFormErrors(validateStreetBox(value, formErrors))
    }

    const onChangeZipCode = (e: ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value
        if (value.length > 4) {
            value = value.trimEnd()
        }
        setStepValues({ ...stepValues, zipCode: value })
        props.onChangeValues && props.onChangeValues({ zipCode: value })
        setFormErrors(validateZipCode(value, true, formErrors))
    }

    const onChangeCity = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        setStepValues({ ...stepValues, city: value })
        props.onChangeValues && props.onChangeValues({ city: value })
        setFormErrors(validateCity(value, formErrors))
    }

    const nextStep = () => {
        validateStep2(stepValues, formErrors).then(
            (errors: FormErrorType[]) => {
                if (errors && errors.length > 0) {
                    setFormErrors(errors)
                } else {
                    setFormErrors([])
                    props.onChangeValues &&
                        props.onChangeValues({ requestInfo: false })
                    props.nextStep && props.nextStep()
                }
            }
        )
    }

    const prevStep = () => {
        props.newRegistration && props.newRegistration()
        props.prevStep && props.prevStep()
    }

    const onCancel = () => {
        setFormErrors([])
        props.onCancel && props.onCancel()
    }

    const uniqueErrors = (key: string) => {
        const errors = formErrors.find(err => err.key === key);
        const uniqueErr = errors ? [...new Set(errors.messages)] : [];

        return uniqueErr.length > 0 ? uniqueErr.join(', ') : '';
    }

    return (
        <div className='form-step-wrapper form-step-wrapper--wide'>
            <h3>
                {t('companyRegistration.customer.association.stepLabel.step2')}
            </h3>
            <p className="required-asterisk mt-3">{t('global.requiredAsterisk')}</p>
            {props.cmsContent ? (
                <div className='form-step-wrapper__content'>
                    {props.cmsContent}
                </div>
            ) : (
                ''
            )}
            <div className='fields-wrapper'>
                {loadingCompanyInfo && (
                    <div className='fields-wrapper__overlay'>
                        <LoadingIndicator />
                    </div>
                )}
                <div className='fields-wrapper__field'>
                    <InputText
                        id="org-number"
                        label={t('companyRegistration.organisationNumberLabel')}
                        value={stepValues.organizationNumber}
                        hasError={
                            !!some(formErrors, ['key', 'organizationNumber'])
                        }
                        disabled={true}
                        required={true}
                        onChange={() => {
                            // empty
                        }}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="legal-name"
                        label={t(
                            'companyRegistration.customer.association.associationName'
                        )}
                        value={stepValues.associationName || ''}
                        hasError={
                            !!some(formErrors, ['key', 'associationName'])
                        }
                        disabled={disableAssociationName}
                        required={true}
                        onChange={onChangeAssociationName}
                        maxLength={50}
                        errorText={uniqueErrors('associationName')}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputToggleButton
                        id="f-tax"
                        className="toggle-field"
                        label={`${t('companyRegistration.fTax')}`}
                        required={true}
                        options={[
                            {
                                value: 'true',
                                label: t('companyRegistration.yes'),
                            },
                            {
                                value: 'false',
                                label: t('companyRegistration.no'),
                            },
                        ]}
                        value={stepValues.registeredForFTax}
                        onSelect={onChangeFTax}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputToggleButton
                        id="registered-for-vat"
                        label={`${t('companyRegistration.registeredForVat')}`}
                        required={true}
                        className="toggle-field"
                        options={[
                            {
                                value: 'true',
                                label: t('global.yes'),
                            },
                            {
                                value: 'false',
                                label: t('global.no'),
                            },
                        ]}
                        disabled={disableVat}
                        value={stepValues.registeredForVAT}
                        onSelect={onChangeRegisteredForVat}
                    />
                </div>
                {stepValues.registeredForVAT === 'true' && (
                    <div className='fields-wrapper__field'>
                        <InputText
                            id="vat-number"
                            label={t('companyRegistration.customer.vatNumber')}
                            value={stepValues.vatNumber || ''}
                            hasError={!!some(formErrors, ['key', 'vatNumber'])}
                            required={true}
                            maxLength={14}
                            disabled={disableVatNumber}
                            onChange={onChangeVatNumber}
                            autoComplete="none"
                            errorText={uniqueErrors('vatNumber')}
                        />
                    </div>
                )}
                <div className='fields-wrapper__field'>
                    <InputToggleButton
                        id="account-type"
                        label={t('companyRegistration.customer.accountType')}
                        required={true}
                        className="toggle-field"
                        hasError={!!some(formErrors, ['key', 'accountType'])}
                        options={[
                            {
                                value: AccountType.Bg,
                                label: t(
                                    'companyRegistration.customer.bankgiro'
                                ),
                            },
                            {
                                value: AccountType.Pg,
                                label: t(
                                    'companyRegistration.customer.plusgiro'
                                ),
                            },
                        ]}
                        value={stepValues.accountType || ''}
                        onSelect={onChangeAccountType}
                        errorText={uniqueErrors('accountType')}
                    />
                </div>
                {stepValues.accountType === AccountType.Bg && (
                    <div className='fields-wrapper__field'>
                        <InputText
                            id="bg"
                            label={t(
                                'companyRegistration.customer.bankgiroAbbreviation'
                            )}
                            value={stepValues.bg || ''}
                            required={true}
                            onChange={onChangeBg}
                            autoComplete="off"
                            hasError={!!some(formErrors, ['key', 'bg'])}
                            errorText={uniqueErrors('bg')}
                        />
                    </div>
                )}
                {stepValues.accountType === AccountType.Pg && (
                    <div className='fields-wrapper__field'>
                        <InputText
                            id="pg"
                            label={t(
                                'companyRegistration.customer.plusgiroAbbreviation'
                            )}
                            value={stepValues.pg || ''}
                            required={true}
                            onChange={onChangePg}
                            autoComplete="off"
                            hasError={!!some(formErrors, ['key', 'pg'])}
                            errorText={uniqueErrors('pg')}
                        />
                    </div>
                )}
                <h5 className='fields-wrapper__title'>
                    {t('companyRegistration.customer.postalAddress')}
                </h5>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="care-of"
                        label={t('companyRegistration.customer.careOf')}
                        value={stepValues.careOf || ''}
                        autoComplete="street-address"
                        onChange={onChangeCareOf}
                        maxLength={100}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="street-box"
                        label={t('companyRegistration.customer.streetBox')}
                        value={stepValues.streetBox || ''}
                        required={true}
                        onChange={onChangeStreetBox}
                        autoComplete="address-line1"
                        hasError={!!some(formErrors, ['key', 'streetBox'])}
                        maxLength={100}
                        errorText={uniqueErrors('streetBox')}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="zip-code"
                        label={t('companyRegistration.zipCode')}
                        value={stepValues.zipCode || ''}
                        required={true}
                        onChange={onChangeZipCode}
                        autoComplete="postal-code"
                        hasError={!!some(formErrors, ['key', 'zipCode'])}
                        errorText={uniqueErrors('zipCode')}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="city"
                        label={t('companyRegistration.city')}
                        value={stepValues.city || ''}
                        required={true}
                        onChange={onChangeCity}
                        autoComplete="city"
                        hasError={!!some(formErrors, ['key', 'city'])}
                        maxLength={50}
                        errorText={uniqueErrors('city')}
                    />
                </div>
            </div>
            {allErrors.length > 0 && (
                <div className='form-step-wrapper__error'>
                    <Alert
                        type="danger"
                        text={t(
                            'companyRegistration.companyInfoContainsErrors'
                        )}
                        subtext={allErrors}
                        compact={false}
                    />
                </div>
            )}
            <div className='buttons-wrapper'>
                <Button
                    type={ButtonType.PRIMARY}
                    className="next-button"
                    text={
                        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--next'>
                            <span>{t('button.next')}</span>
                            <Icon iconName="arrow-right" />
                        </span>
                    }
                    onClick={nextStep}
                />
                <Button
                    type={ButtonType.PRIMARY}
                    className="prev-button"
                    text={
                        <span  className='buttons-wrapper__button-text buttons-wrapper__button-text--prev'>
                            <Icon iconName="arrow-left" />
                            <span>{t('button.previous')}</span>
                        </span>
                    }
                    onClick={prevStep}
                />
                <Button
                    type={ButtonType.SECONDARY}
                    className="cancel-button"
                    text={
                        <span className='buttons-wrapper__button-text'>
                            <Icon iconName="close" />
                            <span>{t('button.cancel')}</span>
                        </span>
                    }
                    onClick={onCancel}
                />
            </div>
        </div>
    )
}

export default Step2